import React, { useEffect, useRef } from "react";
import config from "config";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { useOktaAuth } from "@okta/okta-react";

import { getTenantFromHostname } from "shared/utils";
import { validateCurrentTenantAccess } from "shared/utils/tenant-access-utils";

import AccessForbidden from "features/auth/AccessForbidden";

import { useQuery } from "services/hooks";

import login, { getTrackingEmailFromIDToken } from "./utils";

interface Props {
  children: React.ReactElement;
}

const SecureComponent = ({ children }: Props) => {
  const { authState, oktaAuth } = useOktaAuth();
  const ldClient = useLDClient();
  const loginInitiated = useRef(false);
  const {
    redirect = window.location.pathname + window.location.search,
    autoLogin,
    iss,
  }: { redirect?: string; autoLogin?: string; iss?: string } = useQuery();

  const { singleIDPLogin } = config.get();
  const shouldRedirectUnauthenticated =
    singleIDPLogin?.optionalAutoLogin &&
    singleIDPLogin.unauthenticatedRedirect &&
    !iss &&
    !autoLogin &&
    !config.isInstanceManagement();

  const needsLogin = authState?.isAuthenticated === false && !authState?.error;

  const idToken = authState?.idToken;

  useEffect(() => {
    if (!ldClient || !idToken) return;

    const email = getTrackingEmailFromIDToken(idToken);

    ldClient.identify({
      key: email,
      kind: "user",
      email,
      tenant: getTenantFromHostname(),
    });
  }, [idToken, ldClient]);

  useEffect(() => {
    if (!needsLogin || shouldRedirectUnauthenticated || loginInitiated.current)
      return;

    loginInitiated.current = true;
    login({ oktaAuth, redirect });
  }, [needsLogin, shouldRedirectUnauthenticated, oktaAuth, redirect]);

  const tenants = (authState?.accessToken?.claims?.tenants || []) as string[];
  const hasTenantAccess = validateCurrentTenantAccess(tenants);

  // If authenticated but no tenant access, show forbidden error
  if (authState?.isAuthenticated && !hasTenantAccess) {
    return <AccessForbidden oktaAuth={oktaAuth} />;
  }

  // Without !authState we get redirected to default page after /login/callback
  // but we want to stay on a page we were at if refreshing.
  // Also, when logged out, we also get error state and shouldn't start logging back in immediately
  // as otherwise postLogoutRedirectUri does not work deterministically
  if (!authState || authState?.error) {
    return null;
  } else if (!authState?.isAuthenticated) {
    // For some tenants we want to turn on auto login optionally by adding query parameter autoLogin. If user does
    // not have valid session with Okta, we redirect them to tenant specific page where they can perform login.
    if (shouldRedirectUnauthenticated) {
      window.location.href = singleIDPLogin.unauthenticatedRedirect as string;

      return null;
    }

    return null;
  }

  return children;
};

export default SecureComponent;
